import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle, Download, ArrowLeft } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function ImportFamilies() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/families/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} families!`);
      } else {
        toast.warning('No families were imported. Check the errors below.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import file');
      setResult({
        imported: 0,
        skipped: 0,
        errors: [error.response?.data?.detail || 'Unknown error occurred']
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create a simple CSV template that can be opened in Excel
    const headers = [
      'parent_first_name',
      'parent_last_name',
      'parent_role',
      'email',
      'phone',
      'location_type',
      'ward_number',
      'city_county',
      'address',
      'child_name',
      'child_age',
      'child_gender',
      'child_learning_stage',
      'child_reading_level',
      'child_interests',
      'child_library_card',
      'already_has_bookshelf',
      'books_from_birth_signup',
      'books_count',
      'annual_income',
      'photo_consent',
      'referral_source'
    ];
    
    const sampleRow = [
      'John',
      'Doe',
      'Father',
      'john.doe@example.com',
      '202-555-0123',
      'DC Ward',
      '7',
      '',
      '123 Main St, Washington DC 20001',
      'Jane Doe',
      '5',
      'girl',
      'Early Learning',
      'Developing',
      'animals, nature',
      'no',
      'no',
      'no',
      '1-10',
      'Under $30,000',
      'yes',
      'School'
    ];
    
    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'family_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded! Open in Excel and save as .xlsx');
  };

  return (
    <div className="flex">
      <Sidebar />
      <div className="ml-64 flex-1 p-8" data-testid="import-families-page">
        <div className="max-w-3xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/families')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Families
          </Button>

          <div className="mb-8">
            <h1 className="text-4xl font-bold text-foreground mb-2">Import Families</h1>
            <p className="text-stone-600">Upload an Excel file to bulk import family data</p>
          </div>

          <div className="space-y-6">
            {/* Template Download */}
            <Card className="border-stone-200 shadow-card">
              <CardHeader>
                <CardTitle className="text-lg">Step 1: Prepare Your Data</CardTitle>
                <CardDescription>Download the template and fill in your family data</CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  onClick={downloadTemplate}
                  className="w-full"
                  data-testid="download-template-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Template (CSV)
                </Button>
                <div className="mt-4 p-4 bg-stone-50 rounded-lg">
                  <p className="text-sm font-medium mb-2">Required Columns:</p>
                  <div className="flex flex-wrap gap-2">
                    {['parent_first_name', 'parent_last_name', 'email', 'phone', 'address'].map(col => (
                      <span key={col} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                        {col}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm font-medium mt-4 mb-2">Optional Columns:</p>
                  <div className="flex flex-wrap gap-2">
                    {['parent_role', 'location_type', 'ward_number', 'city_county', 'child_name', 'child_age', 'books_count', 'annual_income', 'referral_source'].map(col => (
                      <span key={col} className="px-2 py-1 bg-stone-200 text-stone-700 text-xs rounded">
                        {col}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* File Upload */}
            <Card className="border-stone-200 shadow-card">
              <CardHeader>
                <CardTitle className="text-lg">Step 2: Upload Excel File</CardTitle>
                <CardDescription>Select your completed Excel file (.xlsx or .xls)</CardDescription>
              </CardHeader>
              <CardContent>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".xlsx,.xls"
                  className="hidden"
                  data-testid="file-input"
                />
                
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    file ? 'border-primary bg-primary/5' : 'border-stone-300 hover:border-primary hover:bg-stone-50'
                  }`}
                >
                  {file ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileSpreadsheet className="w-10 h-10 text-primary" />
                      <div className="text-left">
                        <p className="font-medium">{file.name}</p>
                        <p className="text-sm text-stone-500">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-stone-400 mx-auto mb-3" />
                      <p className="text-stone-600 mb-1">Click to select Excel file</p>
                      <p className="text-sm text-stone-400">Supports .xlsx and .xls formats</p>
                    </>
                  )}
                </div>

                <Button
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="w-full mt-4 rounded-full"
                  data-testid="upload-button"
                >
                  {uploading ? 'Importing...' : 'Import Families'}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            {result && (
              <Card className={`border-2 ${result.imported > 0 ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {result.imported > 0 ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                    )}
                    Import Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="p-4 bg-white rounded-lg text-center">
                      <p className="text-3xl font-bold text-green-600">{result.imported}</p>
                      <p className="text-sm text-stone-600">Imported</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg text-center">
                      <p className="text-3xl font-bold text-yellow-600">{result.skipped}</p>
                      <p className="text-sm text-stone-600">Skipped</p>
                    </div>
                  </div>

                  {result.errors && result.errors.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-stone-700 mb-2">
                        Errors ({result.errors.length}):
                      </p>
                      <div className="max-h-40 overflow-y-auto bg-white rounded-lg p-3">
                        {result.errors.map((error, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm text-red-600 mb-1">
                            <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>{error}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.imported > 0 && (
                    <Button
                      onClick={() => navigate('/families')}
                      className="w-full mt-4 rounded-full"
                    >
                      View Imported Families
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            <Card className="border-stone-200 bg-stone-50">
              <CardContent className="pt-4">
                <p className="text-sm font-medium mb-2">Notes:</p>
                <ul className="text-sm text-stone-600 space-y-1 list-disc list-inside">
                  <li>First row should contain column headers</li>
                  <li>Email must be unique - duplicates will be skipped</li>
                  <li>Boolean fields accept: yes/no, true/false, 1/0</li>
                  <li>Location type should be: "DC Ward", "Maryland", or "Virginia"</li>
                  <li>For multiple children, create separate rows for the same family</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
